import { createContext, useContext, useEffect, useMemo, useState } from "react";

type ThemeSource = "system" | "dark" | "light";
type ThemeName = "dark" | "light";

type ThemeColors = {
    primary: string;
    onPrimary: string;
    secondary: string;
    onSecondary: string;
    disabled: string;
    onDisabled: string;
    surface: string; /* like cards and other containers */
    onSurface: string;
    background: string; /* app background */
    onBackground: string;
    warning: string;
    onWarning: string;
    error: string;
    onError: string;
    success: string;
    onSuccess: string;
    titleTextColor: string;
};
const darkColors: ThemeColors = {
    primary: "#6366F1", // Indigo - vibrant, modern
    onPrimary: "#FFFFFF",
    secondary: "#22D3EE", // Cyan accent
    onSecondary: "#0A0A0F",
    disabled: "#3A3A45",
    onDisabled: "#6B6B78",
    surface: "#1A1A22", // Slightly lifted from background
    onSurface: "#E4E4E9",
    background: "#0D0D12", // Deep near-black
    onBackground: "#F2F2F5",
    warning: "#FBBF24",
    onWarning: "#1A1A22",
    error: "#F87171",
    onError: "#1A1A22",
    success: "#34D399",
    onSuccess: "#0A0A0F",
    titleTextColor: "#FFFFFF",
};

const lightColors: ThemeColors = {
    primary: "#4F46E5", // Deeper indigo for contrast on light bg
    onPrimary: "#FFFFFF",
    secondary: "#0891B2", // Deeper cyan for contrast
    onSecondary: "#FFFFFF",
    disabled: "#E2E2E9",
    onDisabled: "#9494A1",
    surface: "#FFFFFF",
    onSurface: "#1C1C24",
    background: "#F5F5F9", // Soft off-white, not stark
    onBackground: "#14141A",
    warning: "#D97706",
    onWarning: "#FFFFFF",
    error: "#DC2626",
    onError: "#FFFFFF",
    success: "#059669",
    onSuccess: "#FFFFFF",
    titleTextColor: "#0F0F14",
};
type Theme = {
    source: ThemeSource;
    colors: ThemeColors;
    dark: boolean;
    setThemeSource: (themeSource: ThemeSource) => void;
};
const themeContext = createContext<null | Theme>(null);

const themeStorageKey = "Theme";
const getThemeSource = () => {
    const stored = localStorage.getItem(themeStorageKey);
    if (stored) {
        return stored as ThemeSource;
    }
    return "system";
};

function getSystemTheme(): ThemeName {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}

export const ThemeProvider = (props: { children: React.ReactNode }) => {
    const [themeSource, setThemeSource] = useState(() => {
        return getThemeSource();
    });
    const [systemTheme, setSystemTheme] = useState(() => {
        return getSystemTheme();
    });
    useEffect(() => {
        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const listener = (event: MediaQueryListEvent) => {
            const currentTheme: ThemeName = event.matches ? "dark" : "light";
            setSystemTheme(currentTheme);
        };
        media.addEventListener("change", listener);
        return () => {
            media.removeEventListener("change", listener);
        };
    }, []);
    const themeName = useMemo((): ThemeName => {
        if (themeSource == "system") {
            return systemTheme;
        }

        return themeSource;
    }, [themeSource, systemTheme]);

    const colors = useMemo(() => {
        if (themeName == "dark") {
            return darkColors;
        }
        return lightColors;
    }, [themeName]);

    return (
        <themeContext.Provider
            value={{
                colors,
                dark: themeName == "dark",
                setThemeSource: (source) => {
                    localStorage.setItem(themeStorageKey, source);
                    setThemeSource(source);
                },
                source: themeSource,
            }}
        >
            {props.children}
        </themeContext.Provider>
    );
};

export const useTheme = () => {
    const theme = useContext(themeContext);
    if (!theme) {
        throw new Error("No theme provider found");
    }
    return theme;
};
